import { isCompanyUser, isShibUser } from '@thro-helpers/users';
import { User } from '@thro-types/users';

import { intersection, uniq } from 'lodash';

import { SubjectType } from 'app/containers/AWPM-FWPM/ModuleSelections/models';
import { Course } from 'types/AppTypes';

import Roles from './enums/Roles';

export enum UserType {
  COMPANY = 'COMPANY',
  SHIBBOLETH = 'SHIBBOLETH',
  INCOMING = 'INCOMING'
}

export const RolesArray = [
  'Studenten',
  'Mitarbeiter',
  'Hiwis',
  'Professoren',
  'Lehrbeauftragte',
  'Externe'
];

type Property = 'entitlement' | 'role';

export class ACLHelper {
  private user: User;

  constructor(user: User) {
    let mappedUser = { ...user };
    if (user && isShibUser(user) && isShibUser(mappedUser)) {
      mappedUser.roles = RolesArray.filter(r =>
        user.entitlements?.some((e: string) => e.includes(`IDM_${r}_`))
      );
    }
    this.user = mappedUser;
  }

  public isSuperUser(): boolean {
    if (!isShibUser(this.user)) {
      return false;
    }
    return this.user.entitlements?.includes(Roles.SUPER_USER);
  }

  public isJobmarketAdmin(): boolean {
    if (!isShibUser(this.user)) {
      return false;
    }
    return this.hasOne('entitlement', Roles.JOBMARKET_ADMIN);
  }

  public isIncomingsAdmin() {
    if (!isShibUser(this.user)) {
      return false;
    }
    return this.hasOne('entitlement', Roles.INCOMINGS_ADMIN);
  }

  public isCompanyUser(companyId?: string | number): boolean {
    if (!isCompanyUser(this.user)) {
      return false;
    }
    if (companyId !== undefined) {
      return !!this.user.company && this.user.company.id.toString() === companyId.toString();
    }

    return isCompanyUser(this.user);
  }

  public isInternationalOfficeUser() {
    return this.user?.type === UserType.INCOMING;
  }

  public isUniversityCompanyUser() {
    if (!isCompanyUser(this.user)) {
      return false;
    }

    return this.user.company?.type === 'HOCHSCHULE';
  }

  /**
   * @param strict Ignores super user entitlement when set
   */
  public isStudent(strict?: boolean): boolean {
    if (!isShibUser(this.user)) {
      return false;
    }
    const studentCheck = this.user.roles?.includes(Roles.STUDENT) || false;
    if (strict) {
      return studentCheck;
    }
    return studentCheck || this.isSuperUser();
  }

  public isTestUser() {
    return isShibUser(this.user) && this.user.dev;
  }

  /**
   * @param strict Ignores super user entitlement when set
   */
  public isEmployee(strict?: boolean) {
    if (this.user?.type !== UserType.SHIBBOLETH) {
      return false;
    }
    const hasRole = this.hasOne('role', [
      Roles.EMPLOYEE_PROFESSOR,
      Roles.EMPLOYEE,
      Roles.HIWI,
      Roles.LECTURER,
      Roles.PROFESSOR
    ]);

    if (strict) {
      return hasRole;
    }

    return hasRole || this.isSuperUser();
  }

  /**
   * @param strict Ignores super user entitlement when set
   */
  public isProf(strict?: boolean) {
    if (!isShibUser(this.user)) {
      return false;
    }
    const hasRole = this.hasOne('role', [Roles.PROFESSOR]);
    if (strict) {
      return hasRole;
    }

    return hasRole || this.isSuperUser();
  }

  /**
   * Checks if the user has a certain property
   * @param property "entitlement" or "role"
   * @param strict Ignores super user entitlement when set
   */
  public hasOne(property: Property, propertyValue?: string | string[]): boolean {
    if (!propertyValue || !isShibUser(this.user)) {
      return false;
    }
    switch (property) {
      case 'entitlement':
        if (Array.isArray(propertyValue)) {
          return (
            intersection(uniq([...propertyValue, Roles.SUPER_USER]), this.user.entitlements)
              .length > 0
          );
        }
        return this.isSuperUser() || this.user.entitlements.includes(propertyValue);
      case 'role':
        if (Array.isArray(propertyValue)) {
          return (
            intersection(uniq([...propertyValue, Roles.SUPER_USER]), this.user.roles).length > 0
          );
        }
        return this.isSuperUser() || this.user.roles?.includes(propertyValue) || false;
      default:
        return this.isSuperUser();
    }
  }

  public hasAll(property: Property, propertyValues: string[]) {
    if (!isShibUser(this.user)) {
      return false;
    }
    const shibUser = this.user;

    switch (property) {
      case 'entitlement':
        return propertyValues.every(v => shibUser.entitlements.includes(v));
      case 'role':
        return propertyValues.every(v => shibUser.roles?.includes(v));
      default:
        return this.isSuperUser();
    }
  }

  public isSecretaryUser(strict: boolean = false) {
    if (!isShibUser(this.user)) {
      return false;
    }
    if (!strict) {
      return (
        this.isSuperUser() ||
        this.user.entitlements.some((entitlement: string): boolean =>
          entitlement.includes('Sekretariat')
        )
      );
    }
    return this.user.entitlements.some((entitlement: string): boolean =>
      entitlement.includes('Sekretariat')
    );
  }

  public isSelectionAdminForCourseOfStudy(courseOfStudy?: string) {
    if (!courseOfStudy) {
      return false;
    }
    return this.hasOne('entitlement', `rp-${courseOfStudy.toUpperCase()}-Dashboard-Wahladmin`);
  }

  public isSubjectSelectionAdmin(
    strict: boolean = false,
    subjectType: SubjectType = SubjectType.Fwpm
  ) {
    if (!isShibUser(this.user)) {
      return false;
    }
    if (subjectType === SubjectType.Awpm) {
      if (strict) {
        return !!this.user.entitlements.find(
          (entitlement: string) => entitlement === Roles.AWPM_ADMIN
        );
      }
      return (
        !!this.user.entitlements.find((entitlement: string) => entitlement === Roles.AWPM_ADMIN) ||
        this.isSuperUser()
      );
    } else {
      const isFWPMSelectionAdmin = !!this.user.entitlements.find(
        (entitlement: string) =>
          entitlement !== Roles.AWPM_ADMIN &&
          entitlement.startsWith('rp') &&
          entitlement.endsWith('-Dashboard-Wahladmin')
      );
      if (strict) {
        return isFWPMSelectionAdmin;
      }
      return isFWPMSelectionAdmin || this.isSuperUser();
    }
  }

  public isSelectionAdmin(strict: boolean = false) {
    if (!isShibUser(this.user)) {
      return false;
    }

    if (strict) {
      return !!this.user.entitlements.find(
        (entitlement: string) =>
          entitlement.startsWith('rp') && entitlement.endsWith('-Dashboard-Wahladmin')
      );
    }

    return (
      !!this.user.entitlements.find(
        (entitlement: string) =>
          entitlement.startsWith('rp') && entitlement.endsWith('-Dashboard-Wahladmin')
      ) || this.isSuperUser()
    );
  }

  public getSelectionAdminFaculties(): string[] {
    if (!isShibUser(this.user)) {
      return [];
    }

    const selectionAdminEntitlements = this.user.entitlements.filter(
      (entitlement: string) =>
        entitlement.startsWith('rp') && entitlement.endsWith('-Dashboard-Wahladmin')
    );
    return selectionAdminEntitlements?.map(entitlement => entitlement.split('-')[1]);
  }

  public getSelectionAdminCourses(courses: Course[]) {
    const selectionAdminFaculties = this.getSelectionAdminFaculties();
    return courses.filter(
      course =>
        course.faculty?.shorthand && selectionAdminFaculties.includes(course.faculty.shorthand)
    );
  }
}
